import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { KIT_COLOR_SWATCHES, getKitColorName, normalizeHex, isLightSwatchHex } from '../constants/kitColorPalette';

const ColorPickerModal = ({ open, onClose, onColorSelect, activeColorPicker, selectedColors }) => {
  const [activeTab, setActiveTab] = useState(0);

  const colorTabs = [
    { label: 'Color 1', value: 'color1', icon: '1' },
    { label: 'Color 2', value: 'color2', icon: '2' },
    { label: 'Color 3', value: 'color3', icon: '3' }
  ];

  const logoNumberTabs = [
    { label: 'Logo', value: 'color4', icon: 'L' },
    { label: 'Number', value: 'color5', icon: '#' }
  ];
  
  // Sync activeTab with activeColorPicker when modal opens
  useEffect(() => {
    if (open) {
      const isLogoNumberPicker = activeColorPicker === 'color4' || activeColorPicker === 'color5';
      const currentTabs = isLogoNumberPicker ? logoNumberTabs : colorTabs;
      const currentTabIndex = currentTabs.findIndex(tab => tab.value === activeColorPicker);
      if (currentTabIndex !== -1) {
        setActiveTab(currentTabIndex);
      } else {
        // Default to first tab if not found
        setActiveTab(0);
      }
    }
  }, [open, activeColorPicker]);

  const isLogoNumberPicker = activeColorPicker === 'color4' || activeColorPicker === 'color5';
  const currentTabs = isLogoNumberPicker ? logoNumberTabs : colorTabs;
  const currentTabIndex = currentTabs.findIndex(tab => tab.value === activeColorPicker);

  const handleTabChange = (event, newValue) => {
    const selectedTab = currentTabs[newValue];
    setActiveTab(newValue);
    // Update the active color picker
    onColorSelect(selectedTab.value, selectedColors[selectedTab.value]);
  };

  const handleColorSelect = (color) => {
    const currentTab = currentTabs[activeTab];
    onColorSelect(currentTab.value, color);
  };

  const getCurrentColor = () => {
    const currentTab = currentTabs[activeTab];
    return selectedColors[currentTab.value];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          Choose Your Colors
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Color Type Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minHeight: 60,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }
            }}
          >
            {currentTabs.map((tab, index) => (
              <Tab
                key={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: selectedColors[tab.value],
                        border: '2px solid #000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isLightSwatchHex(selectedColors[tab.value]) ? '#000' : '#fff',
                        fontWeight: 'bold'
                      }}
                    >
                      {tab.icon}
                    </Box>
                    <span>{tab.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Color Grid */}
        <Grid container spacing={2}>
          {KIT_COLOR_SWATCHES.map((color) => (
            <Grid item xs={3} sm={2} key={color.value}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  backgroundColor: color.value,
                  border: '2px solid #e0e0e0',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    borderColor: '#1976d2',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }
                }}
                onClick={() => handleColorSelect(color.value)}
              >
                {normalizeHex(getCurrentColor()) === color.value && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: isLightSwatchHex(color.value) ? '#000' : '#fff',
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    ✓
                  </Box>
                )}
                
                {/* Color name tooltip */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    pointerEvents: 'none',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  {color.name}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Current Selection Display */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Current Selection:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                backgroundColor: getCurrentColor(),
                border: '2px solid #000',
                borderRadius: 1
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {getKitColorName(getCurrentColor(), 'Custom Color')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ border: '2px solid #000', color: '#000' }}
        >
          Cancel
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ backgroundColor: '#000', color: '#fff' }}
        >
          Apply Colors
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorPickerModal;

